/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Profile Info Component - Migrated to Shadcn UI
 */
import React, { useContext, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Input, Button, Label, Avatar, AvatarFallback, Spinner } from "@lemonadetraining/web-ui";
import { IconDeviceFloppy, IconUpload } from "@tabler/icons-react";
import { notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";

/* Authentication imports */
import { AuthContext } from "@lemonadetraining/web-shared";
import { messageForUploadError } from "@lemonadetraining/web-shared/utils/imageResize";
import VariantAvatarImage from "@lemonadetraining/web-shared/components/VariantAvatarImage";
import ProfileRoleLine from "./ProfileRoleLine";

interface ProfileInfoProps {
  user: {
    claims?: {
      role?: string;
    };
  };
}

interface AuthContextType {
  user: {
    displayName?: string;
    photoURL?: string;
    email?: string | null;
  } | null;
  uploadUserProfilePic: (file: File) => Promise<string>;
  updateUserProfile: (name: string, photoURL: string) => Promise<void>;
}

const ProfileInfo: React.FC<ProfileInfoProps> = ({ user }) => {
  const authCtx = useContext(AuthContext) as AuthContextType;
  const dispatch = useDispatch();
  const [profileInfoChanged, setProfileInfoChanged] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [photoPreparing, setPhotoPreparing] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const [userPicURL, setUserPicURL] = useState(authCtx.user?.photoURL || "");
  const [userName, setUserName] = useState(authCtx.user?.displayName || "");
  const [userImageFile, setUserImageFile] = useState<File | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  // Desktop and mobile layouts both mount (CSS decides visibility), so each
  // needs its own input for its picker button to target.
  const desktopPhotoInputRef = useRef<HTMLInputElement>(null);
  const mobilePhotoInputRef = useRef<HTMLInputElement>(null);

  const releasePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  // Blob preview URLs live until explicitly revoked; release the last one
  // when the component unmounts.
  useEffect(() => releasePreviewUrl, []);

  // Sync state when authCtx.user updates externally
  useEffect(() => {
    if (authCtx.user?.displayName && !profileInfoChanged) {
      setUserName(authCtx.user.displayName);
    }
    if (authCtx.user?.photoURL && !profileInfoChanged) {
      setUserPicURL(authCtx.user.photoURL);
    }
  }, [authCtx.user?.displayName, authCtx.user?.photoURL, profileInfoChanged]);

  // Generate initials from name
  const getInitials = (name: string): string => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /* ------- [Handlers] --------- */
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      const file = files[0];
      setUserImageFile(file);
      releasePreviewUrl();
      previewUrlRef.current = URL.createObjectURL(file);
      setUserPicURL(previewUrlRef.current);
      setProfileInfoChanged(true);
      setPhotoError(null);
    }
  };

  const handleFullNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserName(event.target.value);
    setProfileInfoChanged(true);
  };

  const handleUpdateProfile = async () => {
    setUpdateInProgress(true);
    setPhotoError(null);
    try {
      // Upload the image to storage if changed
      let downloadURL = userPicURL;
      if (userImageFile) {
        setPhotoPreparing(true);
        try {
          downloadURL = await authCtx.uploadUserProfilePic(userImageFile);
        } catch (error) {
          // Photo failures get an honest, specific message — the generic
          // profile-update string must never describe a photo problem.
          setPhotoError(
            messageForUploadError(
              error,
              "We couldn't upload your photo. Check your connection and try again."
            )
          );
          return;
        } finally {
          setPhotoPreparing(false);
        }
      }

      await authCtx.updateUserProfile(userName, downloadURL);
      releasePreviewUrl();
      setUserPicURL(downloadURL);
      setUserImageFile(null);

      dispatch(
        notifyActions.sendNotification({
          type: NOTIFICATION_TYPE.success,
          message: "Your profile information updated",
        })
      );
      setProfileInfoChanged(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      dispatch(
        notifyActions.sendNotification({
          type: NOTIFICATION_TYPE.error,
          message: "Failed to update profile. Please try again.",
        })
      );
    } finally {
      setUpdateInProgress(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Desktop Layout */}
      <div className="hidden md:flex flex-row gap-6 items-start">
        {/* Avatar Upload Section */}
        <div className="flex flex-col items-center gap-2">
          <input
            ref={desktopPhotoInputRef}
            accept="image/*"
            id="profile-picture-upload"
            data-testid="profile-photo-input"
            type="file"
            className="hidden"
            tabIndex={-1}
            disabled={updateInProgress}
            onChange={handleFileInputChange}
          />
          <button
            type="button"
            aria-label="Change profile photo"
            disabled={updateInProgress}
            onClick={() => desktopPhotoInputRef.current?.click()}
            className="cursor-pointer group rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <div className="relative">
              <Avatar size="lg" className="w-28 h-28">
                <VariantAvatarImage src={userPicURL} alt={userName} />
                <AvatarFallback className="text-2xl">{getInitials(userName)}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center bg-foreground/60 rounded-full opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
                <IconUpload className="h-6 w-6 text-background" />
              </div>
            </div>
          </button>
          <p className="text-xs text-muted-foreground">Click to upload</p>
        </div>

        {/* Form Fields */}
        <div className="flex flex-col gap-3 flex-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              type="text"
              onChange={handleFullNameChange}
              value={userName}
              placeholder="Enter your full name..."
            />
          </div>
          <ProfileRoleLine claimRole={user.claims?.role} />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="flex md:hidden flex-col gap-4 items-center">
        {/* Avatar Upload Section */}
        <div className="flex flex-col items-center gap-2">
          <input
            ref={mobilePhotoInputRef}
            accept="image/*"
            id="profile-picture-upload-mobile"
            data-testid="profile-photo-input-mobile"
            type="file"
            className="hidden"
            tabIndex={-1}
            disabled={updateInProgress}
            onChange={handleFileInputChange}
          />
          <button
            type="button"
            aria-label="Change profile photo"
            disabled={updateInProgress}
            onClick={() => mobilePhotoInputRef.current?.click()}
            className="cursor-pointer group rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <div className="relative">
              <Avatar size="lg" className="w-24 h-24">
                <VariantAvatarImage src={userPicURL} alt={userName} />
                <AvatarFallback className="text-xl">{getInitials(userName)}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center bg-foreground/60 rounded-full opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
                <IconUpload className="h-5 w-5 text-background" />
              </div>
            </div>
          </button>
          <p className="text-xs text-muted-foreground">Tap to upload</p>
        </div>

        {/* Form Fields */}
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="fullNameMobile">Full Name</Label>
            <Input
              id="fullNameMobile"
              type="text"
              onChange={handleFullNameChange}
              value={userName}
              placeholder="Enter your full name..."
            />
          </div>
          <ProfileRoleLine claimRole={user.claims?.role} />
        </div>
      </div>

      {authCtx.user?.email && (
        <div className="flex flex-col gap-0.5 text-sm" data-testid="profile-email">
          <span className="text-xs text-muted-foreground">Email</span>
          <span className="break-all">{authCtx.user.email}</span>
          <span className="text-xs text-muted-foreground">
            You sign in with it. It can&apos;t be changed here.
          </span>
        </div>
      )}

      {/* Photo status + error. The live region is always mounted so screen
          readers announce the status text when it appears — progress is
          conveyed by visible text, never by the spinner or color alone. */}
      <div role="status" aria-live="polite">
        {photoPreparing && (
          <p
            data-testid="photo-status"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <Spinner className="h-4 w-4" aria-hidden="true" />
            Getting your photo ready…
          </p>
        )}
      </div>
      {photoError && (
        <p role="alert" data-testid="photo-error" className="text-sm text-destructive">
          {photoError}
        </p>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          data-testid="profile-save-button"
          disabled={!profileInfoChanged || updateInProgress}
          onClick={handleUpdateProfile}
        >
          {updateInProgress ? (
            <Spinner className="h-4 w-4 mr-2" />
          ) : (
            <IconDeviceFloppy className="h-4 w-4 mr-2" />
          )}
          Save
        </Button>
      </div>
    </div>
  );
};

export default ProfileInfo;
